// İSG Portal — Araç Sheet'i (kayıt sekmesi ve izin akışı ortak; SP ve EP). Açıklama başlıkta değil
// GÖVDEDE (500 karakter belgeleri aşağı itmesin). Belge listesi portala göre değişir (SP yükleme,
// EP bağla + onayla/reddet) → `documents` slot'u. EP, açık bir red satırı varsa Esc'in Sheet'i değil
// satırı kapatması için `onEscapeKeyDown={escapeStaysInReviewRow}` geçer.
//
// Eksik araç belgesi etiketleri §27 kaydından gelir (`label('vehicle', code)`; kayıt yoksa ham kod)
// — iki portal da aynı etiketi görür, uygulama i18n anahtarı yoktur (GL #7). Kayıtta olmayan ya da
// pasif bir türün eksiği hiç karşılanamaz (§27.0(6)); bunu söyleyen not portala özgüdür (SP: "İSG
// uzmanınız giderir") → `inactiveMissingNote` (listenin altında bir kez) ve
// `inactiveMissingSuffix` (etiket yanında) verilmezse (EP) etiket tek başına kalır.
//
// KURAL (#5/#6 tabanı): `--z-popover` katmanı tokens.css'te tanımlanana kadar bu Sheet'in (ve
// `VehicleFormSheet` / `PermitVehicleAddSheet`'in) İÇİNE `RowActionsMenu`, `SelectMenu` veya
// `Tooltip` konmaz — çekmecenin altında kalırlar. Vazgeçme onayı `Modal` olarak kalır.

import { Lock } from 'lucide-react';
import type { ReactNode, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import type { WorkPermitVehicleDto } from '../api/permits';
import type { ContractorVehicleDto } from '../api/vehicles';
import { useDocumentTypes } from '../documents/useDocumentTypes';
import { type VehicleActor, canEditPlate } from '../permits/vehicleRegistry';
import { Sheet } from '../ui/Sheet';
import { Tag } from '../ui/Tag';

export type VehicleSheetContext =
  | { kind: 'registry'; vehicle: ContractorVehicleDto }
  | { kind: 'permit'; vehicle: WorkPermitVehicleDto };

export interface VehicleSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `contractor` (SP) | `host` (EP) — plaka kilidi bu role göre okunur. */
  actor: VehicleActor;
  /** `null` → Sheet kapalı ya da hedef henüz seçilmemiş. */
  context: VehicleSheetContext | null;
  /** Açan satırın "Plaka" düğmesinin referansı — kapanışta odak buraya döner. */
  restoreFocusRef?: RefObject<HTMLElement | null>;
  /** EP `escapeStaysInReviewRow` geçer: açık red satırı varsa Esc Sheet'i değil satırı kapatır. */
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  /** Portalın belge listesi — SP yükleme, EP bağla + onayla/reddet. */
  documents: ReactNode;
  /**
   * İzin bağlamında, kayıtta olmayan/pasif eksik türün etiketinin yanına basılan soluk not (yüklenemez,
   * kim çözer). Verilmezse not yok.
   */
  inactiveMissingNote?: string;
  /** Pasif/bilinmeyen eksik türün etiketinin yanındaki kısa son ek (örn. "(pasif)"); not ise listenin altında BİR kez. */
  inactiveMissingSuffix?: string;
}

export function VehicleSheet({
  open,
  onOpenChange,
  actor,
  context,
  restoreFocusRef,
  onEscapeKeyDown,
  documents,
  inactiveMissingNote,
  inactiveMissingSuffix,
}: VehicleSheetProps) {
  const { t } = useTranslation();
  const { label, inactiveOrUnknown } = useDocumentTypes();
  const vehicle = context?.vehicle ?? null;
  // Kilit: kayıt → rol-nötr olgulardan; izin → bağlı olmak tanımı gereği + iznin kendi isPlateLocked'ı.
  const plateLocked = context
    ? context.kind === 'registry'
      ? !canEditPlate(context.vehicle, actor)
      : !canEditPlate({ hasBeenLinked: true, isPlateLocked: context.vehicle.isPlateLocked }, actor)
    : false;
  const openPermits = context?.kind === 'registry' && context.vehicle.hasOpenPermits;

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
      title={vehicle?.plate ?? ''}
      closeLabel={t('common.close')}
      restoreFocusRef={restoreFocusRef}
      onEscapeKeyDown={onEscapeKeyDown}
    >
      {context && vehicle ? (
        <div className="flex flex-col gap-5">
          {vehicle.description ? (
            <p className="whitespace-pre-line break-words text-sm text-fg">{vehicle.description}</p>
          ) : null}
          {plateLocked || openPermits ? (
            <ul className="flex flex-col gap-1 text-xs text-muted">
              {plateLocked ? (
                <li className="inline-flex items-center gap-1.5">
                  <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('vehicles.sheet.plateLocked')}
                </li>
              ) : null}
              {openPermits ? <li>{t('vehicles.openPermits.yes')}</li> : null}
            </ul>
          ) : null}
          {context.kind === 'permit' ? (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <Tag color={context.vehicle.vehicleReady ? 'green' : 'red'}>
                  {context.vehicle.vehicleReady
                    ? t('permits.vehicles.ready')
                    : t('permits.vehicles.notReady')}
                </Tag>
              </div>
              {context.vehicle.missingVehicleDocumentTypes.length > 0 ? (
                <div className="flex flex-col gap-1.5">
                  <p className="text-xs text-muted">{t('permits.vehicles.missingDocuments')}</p>
                  <ul className="flex flex-wrap gap-1.5">
                    {context.vehicle.missingVehicleDocumentTypes.map((type) => (
                      <li
                        key={type}
                        className="flex min-w-0 max-w-full flex-wrap items-baseline gap-x-1.5 gap-y-0.5"
                      >
                        <Tag color="red" wrap>
                          {label('vehicle', type)}
                        </Tag>
                        {inactiveMissingSuffix && inactiveOrUnknown('vehicle', type) ? (
                          <span className="shrink-0 text-xs text-muted">
                            {inactiveMissingSuffix}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                  {inactiveMissingNote &&
                  context.vehicle.missingVehicleDocumentTypes.some((type) =>
                    inactiveOrUnknown('vehicle', type),
                  ) ? (
                    <p className="text-xs text-muted">{inactiveMissingNote}</p>
                  ) : null}
                </div>
              ) : null}
            </>
          ) : null}
          {documents}
        </div>
      ) : null}
    </Sheet>
  );
}
